import {ReactElement, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {IoWalletSharp} from "react-icons/io5";
import {FaGear, FaReceipt} from "react-icons/fa6";
import {BsPersonFill, BsFillBackpackFill} from "react-icons/bs";
import {useNavigate, useLocation } from "react-router";
import EditProfilePictureModal from "@/components/modals/EditProfilePictureModal";
import {BiLogOut} from "react-icons/bi";
import ToggleSwitch from "./ToggleSwitch";
import {IoIosHelpCircle, IoMdPersonAdd} from "react-icons/io";
import {MdDownload, MdLiveTv } from "react-icons/md";
import {RiVipCrownFill, RiVipCrownLine} from "react-icons/ri";
import {FaGift, FaTrophy, FaChartLine, FaBolt} from "react-icons/fa";
import {toggleChatOpen} from "@/redux/meta/metaActions";
import { useStreamerMode } from "@/hooks/useStreamerMode";
import { useGetCurrentSubscriptionTier } from "@/hooks/useGetCurrentSubscriptionTier";

import useOnLogout from "@/hooks/useOnLogout";
import { InviteModal } from "./InviteModal";
import {useGetElectronClientVersion} from "@/hooks/useGetElectronClientVersion";

interface Props {
    renderProfilePicture: () => ReactElement;
    toggleWalletDropdown: () => void;
    hideChallengeSiteMenuItems?: boolean;
    /** Optional callback to override default invite modal behavior */
    onInviteClick?: () => void;
    /** Callback to open the rewards drawer */
    onOpenRewardsDrawer?: () => void;
    /*
     * Player-panel blocks. Optional so every other consumer of this dropdown
     * is untouched — only the top nav passes them, and it passes data it has
     * already fetched rather than making this component fetch again.
     *
     * These carry what used to be loose controls in the bar: the LVL badge's
     * XP progress, the streak flame, the guild badge, and the gift / XP
     * leaderboard / My Activity icons.
     */
    playerPanel?: {
        xp?: { level: number; gained: number; total: number } | null;
        /** The multiplier chip — swapped into the panel; XP took its bar slot. */
        boost?: { value: string; countdown: string } | null;
        streakDays?: number;
        guild?: { name: string; rank?: number | null; yourRank?: number | null; iconUrl?: string } | null;
        /** Anything live that did not win the navbar's slot. */
        inProgress?: Array<{ key: string; label: string; value: string; verb?: string; onClick: () => void }>;
        onOpenLevelRewards?: () => void;
        onOpenXpLeaderboard?: () => void;
        onOpenMyActivity?: () => void;
    };
    /** Hide the "Download DevourPlay" row — for partner-isolated brand hubs that don't promote the app from their nav */
}

function DevourAccountDropdownMenuBody(props: Props): ReactElement {

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: userProfileData} = useGetUserProfile(fullToken);
    const {currentTier, isSubscribed, isPaused, hasActiveTiers} = useGetCurrentSubscriptionTier(fullToken);
    /*
     * Show the tier UI (Free/tier label + Get Access upsell) only when the
     * subscription program is live (purchasable tiers exist) OR the user
     * already has a tier. With no active tiers, the menu shows just the Level
     * next to the avatar and drops the Get Access row entirely.
     */
    const showTierUi = hasActiveTiers || isSubscribed;
    const { streamerModeEnabled, setSteamerMode } = useStreamerMode();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const history = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const {devourLogout} = useOnLogout();
    /*
     * No session gate. This was `isOnOverwolf ? fullToken : undefined`, which
     * meant the "DevourPlay v{x}" support label only ever rendered INSIDE the
     * desktop shell — exactly not where a user is when support asks them what
     * version they're running. The hook now gates itself on the account's
     * hasDesktopApp, so it renders on any device the user is signed in from.
     */
    const {data: electronClientVersion} = useGetElectronClientVersion(fullToken);

    const [
        editProfileModal,
        setEditProfileModal,
    ] = useState<boolean>(false);

    function toggleEditProfileModal() {
        setEditProfileModal(!editProfileModal);
    }

    async function handleLogOut() {
        try {
            await devourLogout();
            // Check if current page requires authentication
            const authRequiredPages = ["/account", "/lootbox", "/collectibles", "/stash", "/load-fuel", "/menu-orders"];
            const requiresAuth = authRequiredPages.some(page =>
                location.pathname.startsWith(page));

            if (requiresAuth) {
                history("/devourplay-discover");
            }
            // Otherwise stay on current page (games, play, devourplay-discover, etc)
        } catch (error) {
            console.error("Logout failed:", error);
            // On error, check same auth requirements
            const authRequiredPages = ["/account", "/lootbox", "/collectibles", "/stash", "/load-fuel", "/menu-orders"];
            const requiresAuth = authRequiredPages.some(page =>
                location.pathname.startsWith(page));

            if (requiresAuth) {
                history("/devourplay-discover");
            }
        }
    }


    return (
        <>
            {fullToken &&
            <EditProfilePictureModal
                isOpen={editProfileModal}
                onClose={toggleEditProfileModal}
                onProfilePictureUpdate={() => {}}
                hideNftOption={props.hideChallengeSiteMenuItems}
            />
            }

            {/* Only render default InviteModal if no custom onInviteClick handler */}
            {!props.onInviteClick && <InviteModal isVisible={showInviteModal} onClose={() => setShowInviteModal(false)} />}
            <div className="devour-top-nav_dropdown_account_profile-info">
                {props.renderProfilePicture()}
                <div className="devour-top-nav_dropdown_account_profile-info_text">
                    <p>
                        {/*
                         * Show nickname only — never the user's real first name.
                         * `formatGoVIPNames` leaks firstName even with Streamer
                         * Mode on (it only masks lastName). For a publicly-visible
                         * nav dropdown the nickname is the right identity. Fall
                         * back to first name + last-initial only if no nickname
                         * has been set yet.
                         */}
                        {userProfileData?.user?.nickname ||
                            (userProfileData?.user?.firstName
                                ? `${userProfileData.user.firstName}${userProfileData?.user?.lastName ? ` ${userProfileData.user.lastName[0]}.` : ""}`
                                : "Player")}
                    </p>
                    {/*
                      * No subline: the display name IS the nickname, so a
                      * handle under it says the same thing twice, and level
                      * lives on the bar's LVL chip.
                      */}
                </div>
            </div>
            {/*
              * One row grammar (anchor player-panel.html, approved
              * 2026-09-13): every item is icon · label · right value or
              * control. No boxed strips, no chips, no section labels — a
              * live thing shows by being present; amber/red values carry
              * its state. Nothing the bar already says is repeated here.
              */}
            {!props.hideChallengeSiteMenuItems && showTierUi &&
                <button
                    className="reset-button devour-top-nav_dropdown_account_prow"
                    onClick={() => history("/subscription-registration")}
                >
                    <span className="devour-top-nav_dropdown_account_prow_ico">
                        {isSubscribed ? <RiVipCrownFill/> : <RiVipCrownLine/>}
                    </span>
                    <span className="devour-top-nav_dropdown_account_prow_lbl">
                        {isSubscribed ? "DevourPlay Access" : "Get Access"}
                    </span>
                    <span className={`devour-top-nav_dropdown_account_prow_val${isSubscribed ? " devour-top-nav_dropdown_account_prow_val--amber" : " devour-top-nav_dropdown_account_prow_val--red"}`}>
                        <b>
                            {isSubscribed
                                ? isPaused ? "Paused" : currentTier?.name?.toUpperCase() ?? ""
                                : "Unlock more"}
                        </b>
                        <span className="devour-top-nav_dropdown_account_chev" aria-hidden="true">›</span>
                    </span>
                </button>
            }

            {props.playerPanel?.boost &&
                <button
                    className="reset-button devour-top-nav_dropdown_account_prow"
                    onClick={() => history("/play")}
                >
                    <span className="devour-top-nav_dropdown_account_prow_ico"><FaBolt/></span>
                    <span className="devour-top-nav_dropdown_account_prow_lbl">XP Boost</span>
                    <span className="devour-top-nav_dropdown_account_prow_val devour-top-nav_dropdown_account_prow_val--amber">
                        <b>{props.playerPanel.boost.value}</b> · {props.playerPanel.boost.countdown}
                    </span>
                </button>
            }

            {!!props.playerPanel?.guild &&
                <button
                    className="reset-button devour-top-nav_dropdown_account_prow"
                    onClick={() => history("/guilds")}
                >
                    {props.playerPanel.guild.iconUrl
                        ? <img className="devour-top-nav_dropdown_account_prow_gicon" src={props.playerPanel.guild.iconUrl} alt="" />
                        : <span className="devour-top-nav_dropdown_account_prow_ico"><FaTrophy/></span>}
                    <span className="devour-top-nav_dropdown_account_prow_lbl">Guild</span>
                    <span className="devour-top-nav_dropdown_account_prow_val">
                        <b>{props.playerPanel.guild.rank ? `#${props.playerPanel.guild.rank}` : "—"}</b> {props.playerPanel.guild.name}
                        <span className="devour-top-nav_dropdown_account_chev" aria-hidden="true">›</span>
                    </span>
                </button>
            }

            {(props.playerPanel?.inProgress ?? []).map((item) =>
                <button
                    key={item.key}
                    className="reset-button devour-top-nav_dropdown_account_prow"
                    onClick={item.onClick}
                >
                    {item.key === "fuelup"
                        ? <img
                            className="devour-top-nav_dropdown_account_prow_fico"
                            src="/images/fuel-flame.png"
                            alt=""
                        />
                        : <span className="devour-top-nav_dropdown_account_prow_ico"><FaBolt/></span>}
                    <span className="devour-top-nav_dropdown_account_prow_lbl">{item.label}</span>
                    <span className="devour-top-nav_dropdown_account_prow_val">
                        {item.value}
                        {item.verb &&
                            <span className="devour-top-nav_dropdown_account_prow_verb">{item.verb}</span>
                        }
                    </span>
                </button>)}

            {!props.hideChallengeSiteMenuItems &&
                <div className="devour-top-nav_dropdown_account_prow devour-top-nav_dropdown_account_prow--static">
                    <span className="devour-top-nav_dropdown_account_prow_ico"><MdLiveTv/></span>
                    <span className="devour-top-nav_dropdown_account_prow_lbl">Streamer mode</span>
                    <div className="devour-top-nav_dropdown_account_prow_tgl">
                        <ToggleSwitch onChange={setSteamerMode} checked={streamerModeEnabled}/>
                    </div>
                </div>
            }

            <div className="devour-top-nav_dropdown_account_rowgrid">
                {props.playerPanel?.onOpenLevelRewards &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={props.playerPanel.onOpenLevelRewards}>
                        <FaGift/><p>Level rewards</p>
                    </button>
                }
                {props.playerPanel?.onOpenXpLeaderboard &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={props.playerPanel.onOpenXpLeaderboard}>
                        <FaTrophy/><p>XP leaderboard</p>
                    </button>
                }
                {!props.hideChallengeSiteMenuItems && props.onOpenRewardsDrawer &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={props.onOpenRewardsDrawer}>
                        <BsFillBackpackFill/><p>My rewards</p>
                    </button>
                }
                {props.playerPanel?.onOpenMyActivity &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={props.playerPanel.onOpenMyActivity}>
                        <FaChartLine/><p>My activity</p>
                    </button>
                }
                {!props.hideChallengeSiteMenuItems
                    ? <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={() => history("/account")}>
                        <FaGear/><p>Account</p>
                    </button>
                    : <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={toggleEditProfileModal}>
                        <BsPersonFill/><p>Edit profile</p>
                    </button>
                }
                {!props.hideChallengeSiteMenuItems &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={props.toggleWalletDropdown}>
                        <IoWalletSharp/><p>Wallets</p>
                    </button>
                }
                {!props.hideChallengeSiteMenuItems &&
                    <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={() => history("/menu-orders")}>
                        <FaReceipt/><p>Order history</p>
                    </button>
                }
                <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={() => props.onInviteClick ? props.onInviteClick() : setShowInviteModal(true)}>
                    <IoMdPersonAdd/><p>Invite a friend</p>
                </button>
                <button className="reset-button devour-top-nav_dropdown_account_rowgrid_row" onClick={() => dispatch(toggleChatOpen(true))}>
                    <IoIosHelpCircle/><p>Help chat</p>
                </button>
                <button
                    className="reset-button devour-top-nav_dropdown_account_rowgrid_row"
                    onClick={() => {
                        const url = "https://devourplay.gg/download";
                        window.open(url, "_blank", "noopener,noreferrer");
                    }}
                >
                    <MdDownload/><p>Download app</p>
                </button>
            </div>

            <button
                className="reset-button devour-top-nav_dropdown_account_footrow"
                onClick={handleLogOut}
            >
                <BiLogOut/>
                <p>Log out</p>
                {electronClientVersion && <em>v{electronClientVersion}</em>}
            </button>
        </>
    );
}

export default DevourAccountDropdownMenuBody;
